import { mdiBarcodeScan, mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import { da } from "date-fns/locale";
import moment from "moment";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { OrderItemModelV2 } from "../../../../../service/models/orders/order_item_model";
import { OrderModel } from "../../../../../service/models/orders/order_model";
import { getOrgNamespaceApi } from "../../../../../service/repos/cms/org_repo";
import { UTCToLocalDate } from "../../../../../utils/date_util";
import { InventoryStatus } from "../../../../../utils/enums";
import InventoryDropDownV2 from "../../../new_ops/components/inventory_drop_down_v2";
import { useOrderDetailCtx } from "../../../../../context_providers/order_detail_context";
import { useModal } from "../../../../../context_providers/modal/modal_context";
import { debounce } from "lodash";
import WarehouseSearchField from "../../../../../erp/inventory/warehouse/warehouse_search_field";
import { WarehouseModel } from "../../../../../erp/inventory/models/warehouse_model";

interface DeviceAllotmentWidgetProps {
  order: OrderModel;

  onCreateOrderDevices: (data: {
    isScanned: boolean;
    device_ids: string[];
    order_id: string;
    status: string;
    org_namespace: string;
    org_name: string;
    warehouse_id?: string;
    warehouse?: WarehouseModel;
    date: Date;
    item_id: string;
    product_id: string;
  }) => void;
  onScannedDevice: (data: { scanned_id: string }) => void;
}
const defaultData: {
  device_ids: string[];
  order_id: string;
  status: string;
  org_namespace: string;
  org_name: string;
  warehouse_id?: string;
  warehouse?: WarehouseModel;
  date: Date;
  item_id: string;
} = {
  device_ids: [],
  order_id: "",
  status: "",
  org_namespace: "",
  org_name: "",
  warehouse_id: "",
  warehouse: undefined,
  date: new Date(),
  item_id: "",
};
const OrderDeviceCreateWidget: FunctionComponent<
  DeviceAllotmentWidgetProps
> = ({ order, onCreateOrderDevices, onScannedDevice }) => {
  const { scannedDevices, setScannedDevices, scannedID, setScannedID } =
    useOrderDetailCtx();
  const { loading } = useModal();
  const [item, setItem] = useState<OrderItemModelV2>();
  const [scan, setScan] = useState<boolean>(false);
  const [data, setData] = useState<{
    device_ids: string[];
    order_id: string;
    status: string;
    org_namespace: string;
    org_name: string;
    warehouse_id?: string;
    warehouse?: WarehouseModel;
    date: Date;
    item_id: string;
  }>(defaultData);

  const [namespaces, setNameSpace] = useState<
    {
      name: string;
      namespace: string;
    }[]
  >([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<
    { device_id: string; name: string }[]
  >([]);
  const scannedInputFieldRef = useRef<HTMLInputElement>(null);
  const [isDeviceSubmitting, setIsDeviceSubmitting] = useState(false);

  useEffect(() => {
    if (scan && scannedInputFieldRef.current) {
      scannedInputFieldRef.current.focus();
    }
  }, [scan]);

  const onScannedDeviceCallback = useCallback(
    debounce(({ scanned_id }: { scanned_id: string }) => {
      onScannedDevice({ scanned_id });
    }, 500),
    [onScannedDevice],
  );

  useEffect(() => {
    if (scannedID.length === 8) {
      onScannedDeviceCallback({ scanned_id: scannedID });
    }
  }, [scannedID]);

  useEffect(() => {
    getOrgNamespaceApi()
      .then((val) => {
        if (val.success) {
          console.log(val.data);

          setNameSpace(val.data);
        } else alert(val.error);
      })
      .catch((e) => alert(e.message));
  }, []);

  let bal = 0;
  if (item) {
    bal = item.qty - item.delivered_qty + item.returned_qty;
  }

  const handleDeleteDevice = (device_id: string) => {
    setScannedDevices((prev) => prev.filter((d) => d.device_id !== device_id));
  };

  const handleCreateOrderFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isDeviceSubmitting) return;

    if (!item?.id) {
      alert("Item id is missing!");
      return;
    }

    const warehouseId = data.warehouse?.id ?? data.warehouse?._id;
    if (!warehouseId) {
      alert("Please select a warehouse!");
      return;
    }

    setIsDeviceSubmitting(true);
    // convert to compatible type
    const scannedMapped: { device_id: string; name: string }[] = scannedDevices
      .filter((d) => d)
      .map((d) => ({
        device_id: d.device_id,
        name: d.device_group,
      }));

    const existingIds = new Set(selectedDeviceIds.map((d) => d.device_id));
    // filter the new devices
    const newDevices = scannedMapped.filter(
      (d) => !existingIds.has(d.device_id),
    );
    const finalSelected = [...selectedDeviceIds, ...newDevices];
    const device_ids = finalSelected.map((d) => d.device_id);

    onCreateOrderDevices({
      ...data,
      isScanned: scan ? true : false,
      item_id: item?.item_id,
      device_ids: device_ids,
      order_id: order.order_id!,
      product_id: item?.product_id ?? "",
      warehouse_id: warehouseId,
      warehouse: data.warehouse,
    });
    setTimeout(() => setIsDeviceSubmitting(false), 2000);
  };

  return (
    <>
      {!item ? (
        <div className=" overflow-auto  w-full">
          <table className="rounded-xl bg-gray-50 w-full ">
            <thead className="">
              <tr>
                <th className="px-3 py-2  text-xs text-right text-black uppercase tracking-wider">
                  Sl.NO
                </th>
                <th className="px-3 py-2  text-xs text-start text-black uppercase tracking-wider">
                  Product
                </th>

                <th className="px-3 py-2  text-xs text-right text-black uppercase tracking-wider">
                  Price
                </th>
                <th className="px-3 py-2  text-xs text-right text-black uppercase tracking-wider">
                  Qty
                </th>
                <th className="px-3 py-2  text-xs text-right text-black uppercase tracking-wider">
                  Delivered Qty
                </th>
                <th className="px-3 py-2  text-xs text-right text-black uppercase tracking-wider">
                  Installed Qty
                </th>
                <th className="px-3 py-2  text-xs text-right text-black uppercase tracking-wider">
                  Returned Qty
                </th>
                <th className="px-3 py-2  text-xs text-right text-black uppercase tracking-wider">
                  Net Qty
                </th>
              </tr>
            </thead>
            <tbody className="h-10 overflow-auto">
              {order?.products?.map((item, i) => (
                <tr
                  key={i}
                  className="hover:bg-gray-200 rounded-md   cursor-pointer"
                  onClick={() => {
                    if (item.qty <= item.delivered_qty - item.returned_qty) {
                      return alert(
                        "You cannot allot device more then the item qty",
                      );
                    }
                    setItem(item);
                  }}
                >
                  <td className="px-3 py-2 text-xs whitespace-nowrap text-right ">
                    {i + 1}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap text-start">
                    {item.name !== undefined
                      ? item.name
                      : (item.item?.name ?? "--")}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap text-right">
                    {"\u20b9"} {item.price}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap text-right">
                    {item.qty}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap text-right">
                    {item.delivered_qty}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap text-right">
                    {item.installed_qty}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap text-right">
                    {item.returned_qty}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap text-right">
                    {item.delivered_qty - item.returned_qty}
                  </td>
                  {/* <td className="px-3 py-2 text-xs whitespace-nowrap text-right">
                    {"\u20b9"} {item.qty * item.price}
                  </td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <form
          onSubmit={handleCreateOrderFormSubmit}
          className={`text-sm w-full grid gap-5 ${
            scan ? "md:grid-cols-2" : "md:grid-cols-1"
          }`}
        >
          {/* Left Column - Form Fields */}
          <div className="md:w-96 flex flex-col">
            <div className="flex justify-between">
              <div>{item.item?.name ?? "--"}</div>
              <div>
                Remaining Qty{" "}
                {item.qty - item.delivered_qty - item.returned_qty}
              </div>
            </div>
            <div className="flex flex-col mt-3">
              <label htmlFor="namespace">Organization*</label>
              <select
                required
                name="namespace"
                id="namespace"
                className="w-full text-xs px-3 p-1 cursor-pointer bg-gray-100 rounded focus:outline-none"
                value={JSON.stringify(
                  data.org_name
                    ? { name: data.org_name, namespace: data.org_namespace }
                    : {},
                )}
                onChange={(e) => {
                  const org = JSON.parse(e.target.value);
                  setData((o) => ({
                    ...o,
                    org_name: org.name,
                    org_namespace: org.namespace,
                  }));
                }}
              >
                <option value="" hidden>
                  Select
                </option>
                {namespaces
                  .sort((a, b) =>
                    a.name
                      ? a.name.localeCompare(b.name)
                      : a.namespace.localeCompare(b.namespace),
                  )
                  .map((n, i) => (
                    <option key={i} value={JSON.stringify(n)}>
                      {n.name ?? n.namespace}
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex flex-col mt-3">
              <label>Warehouse*</label>
              <WarehouseSearchField
                value={data.warehouse}
                placeholder="Search warehouse"
                onSelect={(val) => {
                  setData((o) => ({
                    ...o,
                    warehouse_id: val.id ?? val._id ?? "",
                    warehouse: val,
                  }));
                }}
                onClear={() => {
                  setData((o) => ({
                    ...o,
                    warehouse_id: "",
                    warehouse: undefined,
                  }));
                }}
              />
            </div>

            <h3 className="mt-2">Status*</h3>
            <div className="flex items-center gap-5">
              {["Delivered", "Installed"].map((status) => (
                <div className="flex items-center gap-2" key={status}>
                  <input
                    required
                    type="radio"
                    id={status.toLowerCase()}
                    name="status"
                    value={status}
                    checked={data.status === status}
                    onChange={() => setData((o) => ({ ...o, status }))}
                  />
                  <label htmlFor={status.toLowerCase()}>{status}</label>
                </div>
              ))}
            </div>

            <div className="flex flex-col my-2">
              <label htmlFor="date">Date*</label>
              <input
                required
                name="date"
                type="date"
                id="date"
                className="rounded px-2 py-1 border"
                value={moment(data.date).format("YYYY-MM-DD")}
                onChange={(e) =>
                  setData((o) => ({
                    ...o,
                    date: UTCToLocalDate(e.target.value)!,
                  }))
                }
              />
            </div>

            {scan === false ? (
              <>
                {" "}
                <div>
                  <InventoryDropDownV2
                    status={[
                      InventoryStatus.stocked,
                      InventoryStatus.returnedNStocked,
                    ]}
                    product_id={item.item?.item_code!}
                    old_name={item.name}
                    disabled={bal <= 0 || selectedDeviceIds.length === bal}
                    selected={selectedDeviceIds}
                    onSelect={(v) => setSelectedDeviceIds((o) => [...o, v])}
                  />

                  <div className="flex flex-wrap gap-2 col-span-2 text-xs border-t py-1">
                    {selectedDeviceIds.slice(0, 11).map((d, i) => (
                      <div
                        className="border rounded-3xl p-1 flex gap-1"
                        key={i}
                      >
                        <p>
                          {d.device_id}{" "}
                          <span style={{ fontSize: 8 }}>({d.name})</span>
                        </p>
                        <span
                          onClick={() =>
                            setSelectedDeviceIds((o) =>
                              o.filter((_, j) => j !== i),
                            )
                          }
                          className="hover:text-red-500 cursor-pointer"
                        >
                          <Icon path={mdiClose} size="16" />
                        </span>
                      </div>
                    ))}
                    {selectedDeviceIds.length > 11 && (
                      <div className="border rounded-3xl p-1 flex gap-1">
                        + {selectedDeviceIds.length - 10} more
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center">
                  <hr className="flex-grow border-t border-gray-300" />
                  <span className="mx-2 text-gray-400 text-lg">or</span>
                  <hr className="flex-grow border-t border-gray-300" />
                </div>
              </>
            ) : (
              <></>
            )}

            {/* <button
              onClick={() => setScan((o) => !o)}
              className="border rounded-lg px-3 py-1 text-s bg-myPrimaryColor cursor-pointer"
              type="button"
            >
              <Icon path={mdiBarcodeScan} className="h-5 w-5"></Icon>
              Scan
            </button> */}
            <button
              onClick={() => setScan((o) => !o)}
              type="button"
              className="flex justify-center items-center gap-2 border rounded-lg px-3 py-1 text-sm bg-myPrimaryColor text-black cursor-pointer"
            >
              <Icon path={mdiBarcodeScan} className="h-5 w-5" />
              Scan Barcode
            </button>

            <div className="flex justify-end mt-5 gap-3">
              <button
                onClick={() => setItem(undefined)}
                className="border rounded-lg px-3 py-1 text-sm hover:bg-black hover:text-white cursor-pointer"
                type="button"
              >
                Back
              </button>

              <button
                className="border rounded-lg px-3 py-1 text-sm hover:bg-black hover:text-white cursor-pointer"
                type="submit"
              >
                Submit
              </button>
            </div>
          </div>

          {scan === true && (
            <>
              <section>
                <div className="bg-gray-50 p-3 m-2 border rounded-lg">
                  <input
                    type="text"
                    value={scannedID}
                    placeholder="Scan Barcode"
                    className="font-semibold h-full"
                    ref={scannedInputFieldRef}
                    disabled={loading ? true : false}
                    onChange={(e) => setScannedID(e.target.value)}
                  />
                </div>
                <div className="overflow-auto border rounded-lg">
                  <table className="w-full text-sm text-left table-auto border-collapse">
                    <thead className="bg-gray-100 border-b">
                      <tr>
                        <th className="px-4 py-2">Device ID</th>
                        <th className="px-4 py-2">Device Group</th>
                        <th className="px-4 py-2">Manufacturer ID</th>
                        <th className="px-4 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scannedDevices.map((device) => (
                        // <tr
                        //   key={device.device_id}
                        //   className="border-b hover:bg-gray-50"
                        // >
                        //   <td className="px-4 py-2">{device.device_id}</td>
                        //   <td className="px-4 py-2">{device.device_group}</td>
                        //   <td className="px-4 py-2">
                        //     {device.manufacturer_id}
                        //   </td>
                        //   <td className="px-4 py-2">
                        //     <button
                        //       onClick={() =>
                        //         handleDeleteDevice(device.device_id)
                        //       }
                        //       // onClick={()=>{}}
                        //       className="text-red-600 hover:text-red-800 font-medium"
                        //     >
                        //       Delete
                        //     </button>
                        //   </td>
                        // </tr>
                        <>
                          <tr
                            key={device.device_id}
                            className={`border-b hover:bg-gray-50 ${
                              device.inventory_found === false
                                ? "bg-gray-200"
                                : ""
                            }`}
                          >
                            <td className="px-4 py-2">{device.device_id}</td>
                            <td className="px-4 py-2">{device.device_group}</td>
                            <td className="px-4 py-2">
                              {device.manufacturer_id}
                            </td>
                            <td className="px-4 py-2">
                              <button
                                onClick={() =>
                                  handleDeleteDevice(device.device_id)
                                }
                                className="text-red-600 hover:text-red-800 font-medium"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                          {/* {device.inventory_found === false && (
                            <tr className="bg-red-50">
                              <td
                                colSpan={4}
                                className="px-4 py-2 text-red-600 text-sm"
                              >
                                This device cannot be added because it was not
                                found in inventory
                              </td>
                            </tr>
                          )} */}
                        </>
                      ))}

                      {scannedDevices.length === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            className="text-center py-3 text-gray-500"
                          >
                            No scanned devices.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </form>
      )}
    </>
  );
};

export default OrderDeviceCreateWidget;
